import { Button } from "@capacit-com/lara-law-shared-ui";
import { Input } from "@capacit-com/lara-law-shared-ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@capacit-com/lara-law-shared-ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { Card, CardContent } from "@capacit-com/lara-law-shared-ui";
import { Badge } from "@capacit-com/lara-law-shared-ui";
import { Trash2, ChevronDown, X } from "lucide-react";
import { FieldArrayWithId, UseFormReturn } from "react-hook-form";
import { useState } from "react";

import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@capacit-com/lara-law-shared-ui";
import { FormValues } from "../MetadataTypesView";
import { Spinner } from "@capacit-com/lara-law-shared-ui";

interface MetadataFormContentProps {
  fields: FieldArrayWithId<FormValues, "metadataTypes", "id">[];
  form: UseFormReturn<FormValues>;
  handleDelete: (id: string) => void;
  isTenantLoading: boolean;
  isDeletingMetadata: string | undefined;
}

const MetadataFormContent = ({
  fields,
  form,
  handleDelete,
  isDeletingMetadata,
  isTenantLoading,
}: MetadataFormContentProps) => {
  return (
    <>
      {fields.length > 0 && (
        <div className="flex flex-col gap-2 h-[calc(100vh-14rem)] overflow-y-auto px-2">
          {fields.map((field, index) => {
            const metadataId = form.getValues(`metadataTypes.${index}.id`);
            return (
              <div className="flex gap-2" key={field.id}>
                <FormField
                  control={form.control}
                  name={`metadataTypes.${index}.name`}
                  render={({ field: formField }) => (
                    <FormItem className="flex-1">
                      <FormControl>
                        <Input
                          {...formField}
                          value={formField.value || ""}
                          placeholder="Navn på metadata type"
                          className="flex-1"
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`metadataTypes.${index}.externalPropertyName`}
                  render={({ field: formField }) => {
                    return (
                      <FormItem className="flex-1">
                        <FormControl>
                          <Input
                            {...formField}
                            placeholder="Dokument egenskaber, f.eks. Publisher"
                            className="flex-1"
                            value={formField.value || ""}
                          />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name={`metadataTypes.${index}.selectValues`}
                  render={() => {
                    const currentType = form.watch(
                      `metadataTypes.${index}.type`
                    );

                    if (currentType !== "select") {
                      return <></>; // Don't render this field for non-select types
                    }

                    return (
                      <FormItem className="flex-1">
                        <FormControl>
                          <SelectValuesInput index={index} form={form} />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name={`metadataTypes.${index}.type`}
                  render={({ field: formField }) => (
                    <FormItem>
                      <FormControl>
                        <Select
                          value={formField.value}
                          onValueChange={formField.onChange}
                        >
                          <SelectTrigger className="cursor-pointer w-38">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="cursor-pointer">
                            <SelectItem
                              value="string"
                              className="cursor-pointer"
                            >
                              Tekst
                            </SelectItem>
                            <SelectItem
                              value="number"
                              className="cursor-pointer"
                            >
                              Tal
                            </SelectItem>
                            <SelectItem
                              value="boolean"
                              className="cursor-pointer"
                            >
                              Ja/Nej
                            </SelectItem>
                            <SelectItem
                              value="dateTime"
                              className="cursor-pointer"
                            >
                              Dato
                            </SelectItem>
                            <SelectItem
                              value="select"
                              className="cursor-pointer"
                            >
                              Valg
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
                <Button
                  type="button"
                  onClick={() => {
                    if (metadataId) {
                      handleDelete(metadataId);
                    }
                  }}
                  variant="outline"
                  className="max-w-10.5"
                  disabled={
                    isTenantLoading ||
                    isDeletingMetadata !== undefined ||
                    !metadataId
                  }
                >
                  {isDeletingMetadata !== undefined &&
                  isDeletingMetadata === metadataId ? (
                    <Spinner className="size-4" />
                  ) : (
                    <Trash2 className="size-4" />
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {fields.length === 0 && !isTenantLoading && (
        <Card className="border-dashed border-2 h-[calc(100vh-14rem)] rounded-sm">
          <CardContent className="flex flex-col items-center justify-center h-full text-center py-0">
            <p className="text-muted-foreground mb-4">
              Ingen metadata typer oprettet endnu. Tryk på knappen under for at
              tilføje en.
            </p>
          </CardContent>
        </Card>
      )}
    </>
  );
};

// SelectValuesInput component for managing select options
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SelectValuesInput = ({ index, form }: { index: number; form: any }) => {
  const [inputValue, setInputValue] = useState("");
  const watchedType = form.watch(`metadataTypes.${index}.type`);

  // Only show when type is select
  if (watchedType !== "select") return null;

  const currentValues = form.watch(`metadataTypes.${index}.selectValues`) || [];

  const handleInputChange = (value: string) => {
    setInputValue(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addValue();
    }
  };

  const addValue = () => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue && !currentValues.includes(trimmedValue)) {
      const newValues = [...currentValues, trimmedValue];
      form.setValue(`metadataTypes.${index}.selectValues`, newValues);
      form.trigger(`metadataTypes.${index}.selectValues`);
      setInputValue("");
    }
  };

  const removeValue = (valueToRemove: string) => {
    const newValues = currentValues.filter(
      (value: string) => value !== valueToRemove
    );
    form.setValue(`metadataTypes.${index}.selectValues`, newValues);
    form.trigger(`metadataTypes.${index}.selectValues`);
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-1 px-2 py-1 border border-input rounded-md bg-background min-h-9">
        <Input
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            "Tilføj valgmuligheder (tryk Enter eller , for at tilføje)"
          }
          className="flex-1 border-0 shadow-none focus-visible:ring-0 p-0 h-auto min-w-20"
        />
        {currentValues.length > 0 && (
          <>
            {currentValues.length <= 2 ? (
              <div className="flex items-center gap-1 flex-shrink-0">
                {currentValues.map((value: string, idx: number) => (
                  <Badge
                    key={idx}
                    variant="secondary"
                    className="cursor-pointer text-xs rounded-sm flex-shrink-0"
                    onClick={() => removeValue(value)}
                  >
                    {value} ×
                  </Badge>
                ))}
              </div>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-xs flex-shrink-0"
                  >
                    {currentValues.length} valgmuligheder
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {currentValues.map((value: string, idx: number) => (
                    <DropdownMenuItem
                      key={idx}
                      className="flex items-center justify-between cursor-pointer"
                      onSelect={(e) => {
                        e.preventDefault();
                        removeValue(value);
                      }}
                    >
                      <span className="truncate">{value}</span>
                      <X className="w-3 h-3 ml-2 flex-shrink-0" />
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MetadataFormContent;
